import { inject } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  Router,
  ResolveFn,
  RouterStateSnapshot,
} from '@angular/router';
import { combineLatest, Observable, of, throwError } from 'rxjs';
import { catchError, map, mergeMap } from 'rxjs/operators';

import { Activity } from '~/core/model/activity.model';
import { Timeslot } from '~/core/model/timeslot.model';
import { ActivityService } from '~/core/services/activity/activity.service';
import { OrganizationService } from '~/core/services/organization/organization.service';

import { appConfig } from '~/app.config';
import { SubscriptionSearchService } from '~/attendee/service/subscription-search.service';
import { ReviewStat } from '~/core/model/review-stats.model';
import { SortEnum, TimeslotStatusEnum } from '@sport-activities/sdk';

export const ActivityDetailResolver: ResolveFn<{
  activity: Activity;
  timeslots: Timeslot[];
}> = (
  route: ActivatedRouteSnapshot,
  state: RouterStateSnapshot,
  activityService: ActivityService = inject(ActivityService),
  router: Router = inject(Router),
  organizationService: OrganizationService = inject(OrganizationService),
  subscriptionSearchService: SubscriptionSearchService = inject(
    SubscriptionSearchService
  )
): Observable<{ activity: Activity; timeslots: Timeslot[] }> => {
  const activityId = route.paramMap.get('id');
  const activityIsPreview = route.queryParams.preview === 'true';
  const isDatesPage = activityService.isDatesPage(state.url);

  return activityService
    .getActivity(activityId)
    .pipe(
      catchError((error) => {
        console.error('activity-detail - RESOLVER', error);
        router.navigate(['/404']);

        return throwError(() => error);
      }),
      mergeMap((activity) => {
        // an old activity can exists without any organization
        const organizationObs = activity?.organizationIdentifier
          ? organizationService
              .getOrganizationById(activity.organizationIdentifier)
              .pipe(catchError(() => of(null)))
          : of(null);

        const organizationReviewsStatsObs: Observable<ReviewStat> =
          activity?.organizationIdentifier
            ? organizationService
                .getOrganizationReviewsStats(activity.organizationIdentifier)
                .pipe(catchError(() => of(null)))
            : of(null);

        return combineLatest([
          activityService.getActivityTimeslots(
            activityId,
            isDatesPage
              ? subscriptionSearchService.getTimeslotsFilters()
              : {
                  timeslotStatus: 'PUBLISHED' as TimeslotStatusEnum,
                  excludeFull: true,
                  startDate: new Date().toISOString(),
                  sort: {
                    by: 'startDate',
                    order: 'asc' as SortEnum,
                  },
                },

            1,
            isDatesPage ? appConfig.pagination.timeslot.loadMore : 5
          ),
          of(activity),
          organizationObs,
          organizationReviewsStatsObs,
        ]);
      })
    )
    .pipe(
      map(([timeslots, activity, organization, organizationReviewsStats]) => {
        return {
          activity,
          timeslots,
          organization,
          organizationReviewsStats,
          isPreview: activityIsPreview,
        };
      })
    );
};
